import { useMemo, useEffect, useState } from 'react';
import { isEqual } from 'lodash';

function getMetaQueryOverrideSet(resource, metaQueryOverride, metaQueryValues) {
  if (typeof metaQueryOverride === 'function') return metaQueryOverride({ resource, metaQueryValues });
  return metaQueryOverride;
}

export default function useMetaQueryFieldGroup({ resource, metaQueryName, metaQueryOverride }) {
  // resource meta query object
  const metaQuery = useMemo(() => resource?.meta().query(metaQueryName), [metaQueryName]);

  // meta query values saved as key-value for each meta query field
  const [metaQueryValues, setMetaQueryValues] = useState({});
  useEffect(() => {
    if (!metaQuery) return;

    const metaOverrideSet = getMetaQueryOverrideSet(resource, metaQueryOverride, {});
    if (!metaOverrideSet) return;

    const values = {};
    for (const queryField of metaQuery.fieldList) {
      const metaField = metaOverrideSet.applyOverrides(queryField);
      if (metaField.attr('default')) {
        values[metaField.attr('name')] = metaField.attr('default');
      }
    }
    setMetaQueryValues(values);
  }, [resource]);
  function setMetaQueryValue(name, value) {
    setMetaQueryValues({
      ...metaQueryValues,
      [name]: value,
    });
  }

  // meta override constructed after any updates to the meta query values
  const metaOverrideSet = useMemo(() => getMetaQueryOverrideSet(resource, metaQueryOverride, metaQueryValues), [metaQueryValues]);

  // query fields with the meta override applied to them
  const fields = useMemo(() => {
    if (!metaQuery) return [];

    const _fields = [];
    for (const queryField of metaQuery.fieldList) {
      // include all query fields if meta override does not exist
      if (!metaOverrideSet) {
        _fields.push(queryField);
        continue;
      }

      const metaField = metaOverrideSet.applyOverrides(queryField);
      _fields.push(metaField);
    }

    return _fields;
  }, [metaOverrideSet]);

  // resource self link URL
  const originalResourceLinkURL = useMemo(() => new URL(resource.link('self').url()), [resource.link('self').url()]);

  // URL constructed after any updates
  const updatedResourceLinkURL = useMemo(() => {
    const linkURL = new URL(originalResourceLinkURL);

    for (const field of fields) {
      const key = field.attr('name');

      // remove query param if excluded
      if (field.attr('exclude')) {
        linkURL.searchParams.delete(key);
        continue;
      }

      let value = metaQueryValues[key];
      // If the value has been cleared out, then don't include it in the query params
      if (Object.keys(metaQueryValues).includes(key) && value === null) {
        linkURL.searchParams.delete(key);
        continue;
      }

      // stop building resource link if required field does not have a value
      if (field.attr('required') && !value) {
        return null;
      }

      if (value !== undefined) {
        if (field.attr('type') === 'date') {
          [value] = value.split('T');
        }
        linkURL.searchParams.set(key, value);
      }
    }
    return linkURL;
  }, [metaQueryValues, fields]);

  const isDifferent = useMemo(() => {
    if (!updatedResourceLinkURL) return false;

    return !isEqual(Object.fromEntries(updatedResourceLinkURL.searchParams.entries()), Object.fromEntries(originalResourceLinkURL.searchParams.entries()));
  }, [updatedResourceLinkURL]);

  // facet-rest Link object constructed with query params from updatedResourceLinkURL
  function buildUpdatedResourceLink() {
    if (!updatedResourceLinkURL) return null;

    const link = resource.link('self').copy();
    link.attr('url', updatedResourceLinkURL);
    return link;
  }


  return {
    fields,
    metaQueryValues,
    setMetaQueryValue,
    buildUpdatedResourceLink,
    updatedResourceLinkURL,
    isDifferent,
  };
}
